/**
 * Grid Utility Functions for YearlyCalendar Component
 * Handles calendar grid generation and calculations
 */
import { DayObject, CalendarRow, MonthData, CalendarConfig } from '../../../contracts/YearlyCalendarContracts';
import {
  generateMonthDays,
  getDateKey,
  getDateUid,
  isToday,
  isSameDay,
  isSameMonth,
  isDateInRange,
  isNonWorkDay,
  getWeekNumber,
  format,
} from './dateUtils';

/**
 * Create a DayObject from a Date
 */
export const createDayObject = (
  date: Date,
  activeDate: Date,
  selectedDate: Date | null,
  config: CalendarConfig,
  uidPrefix: string = 'day',
  customClass?: (date: Date) => string | null,
  customAriaLabel?: (date: Date) => string | null,
  customIconClass?: (date: Date) => string | null
): DayObject => {
  const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
  const isCurrent = isToday(date);
  const isSecondary = !isSameMonth(date, activeDate);
  const isDisabled = !isDateInRange(date, config.minDate, config.maxDate);
  
  return {
    date,
    label: format(date, config.formatDay || 'd'),
    selected: isSelected,
    disabled: isDisabled,
    current: isCurrent,
    secondary: isSecondary,
    key: getDateKey(date),
    uid: getDateUid(uidPrefix, date),
    customClass: customClass?.(date) || undefined,
    customAriaLabel: customAriaLabel?.(date) || undefined,
    customIconClass: customIconClass?.(date) || undefined,
    monthIndex: date.getMonth(),
  };
};

/**
 * Generate calendar rows for a month view
 */
export const generateCalendarRows = (
  activeDate: Date,
  selectedDate: Date | null,
  config: CalendarConfig,
  uidPrefix: string = 'day',
  customClass?: (date: Date) => string | null,
  customAriaLabel?: (date: Date) => string | null,
  customIconClass?: (date: Date) => string | null
): CalendarRow[] => {
  const days = generateMonthDays(activeDate, config.startingDay);
  const rows: CalendarRow[] = [];
  
  for (let i = 0; i < 6; i++) {
    const weekDays = days.slice(i * 7, (i + 1) * 7);
    const dayObjects = weekDays.map(date =>
      createDayObject(
        date,
        activeDate,
        selectedDate,
        config,
        uidPrefix,
        customClass,
        customAriaLabel,
        customIconClass
      )
    );
    
    rows.push({
      days: dayObjects,
      weekNumber: config.showWeeks ? getWeekNumber(weekDays[0]) : undefined,
    });
  }
  
  return rows;
};

/**
 * Generate month data for year view (12 months)
 */
export const generateYearMonths = (
  year: number,
  selectedDate: Date | null,
  config: CalendarConfig,
  startMonth: number = 0
): MonthData[] => {
  const months: MonthData[] = [];
  
  for (let i = 0; i < 12; i++) {
    const monthIndex = (startMonth + i) % 12;
    const yearOffset = startMonth + i >= 12 ? 1 : 0;
    const actualYear = year + yearOffset;
    const date = new Date(actualYear, monthIndex, 1);
    const rows = generateCalendarRows(date, selectedDate, config, `month-${monthIndex}`);
    
    // Flatten days from rows (compatible with ES5+)
    const days: DayObject[] = [];
    rows.forEach(r => days.push(...r.days));
    
    months.push({
      month: monthIndex,
      year: actualYear,
      label: format(date, config.formatMonth || 'MMMM'),
      days,
      rows,
    });
  }
  
  return months;
};

/**
 * Generate year picker grid
 */
export const generateYearGrid = (
  activeYear: number,
  columns: number = 5,
  rows: number = 4
): number[][] => {
  const totalYears = columns * rows;
  const startYear = activeYear - Math.floor(totalYears / 2);
  
  const grid: number[][] = [];
  let yearIndex = 0;
  
  for (let row = 0; row < rows; row++) {
    const rowYears: number[] = [];
    for (let col = 0; col < columns; col++) {
      rowYears.push(startYear + yearIndex);
      yearIndex++;
    }
    grid.push(rowYears);
  }
  
  return grid;
};

/**
 * Get year range for year picker display
 */
export const getYearRange = (
  activeYear: number,
  columns: number = 5,
  rows: number = 4
): { start: number; end: number } => {
  const totalYears = columns * rows;
  const startYear = activeYear - Math.floor(totalYears / 2);
  return {
    start: startYear,
    end: startYear + totalYears - 1,
  };
};

/**
 * Calculate popup position based on cell position
 */
export const calculatePopupPosition = (
  rowIndex: number,
  colIndex: number,
  totalRows: number = 6,
  totalCols: number = 7
): 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' => {
  const isTopHalf = rowIndex < totalRows / 2;
  const isLeftEdge = colIndex === 0;
  const isRightEdge = colIndex === totalCols - 1;
  
  let position: 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  
  if (isTopHalf) {
    if (isLeftEdge) position = 'bottom-left';
    else if (isRightEdge) position = 'bottom-right';
    else position = 'bottom';
  } else {
    if (isLeftEdge) position = 'top-left';
    else if (isRightEdge) position = 'top-right';
    else position = 'top';
  }
  
  return position;
};

/**
 * Get row and column index for a date in the grid
 */
export const getDateGridPosition = (
  date: Date,
  rows: CalendarRow[]
): { rowIndex: number; colIndex: number } | null => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    for (let colIndex = 0; colIndex < row.days.length; colIndex++) {
      if (isSameDay(row.days[colIndex].date, date)) {
        return { rowIndex, colIndex };
      }
    }
  }
  return null;
};

/**
 * Find a day object by date key
 */
export const findDayByKey = (rows: CalendarRow[], key: string): DayObject | null => {
  for (const row of rows) {
    for (const day of row.days) {
      if (day.key === key) {
        return day;
      }
    }
  }
  return null;
};

/**
 * Get adjacent date for keyboard navigation
 */
export const getAdjacentDate = (
  currentDate: Date,
  direction: 'up' | 'down' | 'left' | 'right',
  rows: CalendarRow[]
): Date | null => {
  const position = getDateGridPosition(currentDate, rows);
  if (!position) return null;
  
  let { rowIndex, colIndex } = position;
  
  switch (direction) {
    case 'left':
      colIndex -= 1;
      if (colIndex < 0) {
        rowIndex -= 1;
        colIndex = 6;
      }
      break;
    case 'right':
      colIndex += 1;
      if (colIndex > 6) {
        rowIndex += 1;
        colIndex = 0;
      }
      break;
    case 'up':
      rowIndex -= 1;
      break;
    case 'down':
      rowIndex += 1;
      break;
  }
  
  if (rowIndex < 0 || rowIndex >= rows.length) return null;
  if (colIndex < 0 || colIndex >= 7) return null;
  
  return rows[rowIndex].days[colIndex].date;
};
